import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw, Clock, Eye } from 'lucide-react';
import { Table, Space } from 'antd';
import LogViewerModal from '../../../../components/Modals/jenkinsLogs'; // Import the new component
import { apiClient } from '../../../../utils/apiClient';

const Deployment = ({ serviceBasicInfo }) => {
  const { projectId, serviceId } = useParams();
  const [builds, setBuilds] = useState([]);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    fetchBuilds();
    return () => {
      // Clean up any polling when component unmounts
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [serviceId]);

  const fetchBuilds = async () => {
    try {
      const response = await fetch(`https://cloudify-prod.wizr.in/${projectId}/service/${serviceId}/builds`);
      const data = await response.json();
      setBuilds(data.builds);
    } catch (error) {
      console.error('Error fetching builds:', error);
    }
  };

  const startPolling = () => {
    // Clear any existing polling interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    // Set up new polling interval (every 1 second)
    const interval = setInterval(() => {
      fetchBuilds();
    }, 1000);
    
    setPollingInterval(interval);
    
    // Auto-stop polling after a reasonable time (e.g., 2 minutes)
    setTimeout(() => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }, 60000); // 1 minutes timeout
  };

  const startBuild = async () => {
    try {
      setIsLoading(true);
      setButtonDisabled(true);
      
      const data = await apiClient.post(`/${projectId}/service/${serviceId}/trigger-build`, 
        {
          service_name: serviceBasicInfo?.serviceName,
          cluster_name: serviceBasicInfo?.clusterName
        },
        {
          projectId,
          resourceId: serviceId,
          resourceType: 'BACKEND_SERVICE',
          requiredPermission: 'trigger:build'
        }
      );
      
      if (data) {
        
        // Start polling for build updates
        startPolling();
        
        // Re-enable button after 5 seconds
        setTimeout(() => {
          setButtonDisabled(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error triggering build:', error);
      setButtonDisabled(false);
    } finally {
      setIsLoading(true); // Keep loading indicator while polling
      
      // This setTimeout is just to ensure we don't reset isLoading too early
      setTimeout(() => {
        if (!pollingInterval) {
          setIsLoading(false);
        }
      }, 500);
    }
  };

  const viewBuildLogs = (build) => {
    setSelectedBuild(build);
  };

  const calculateDuration = (duration) => {
    const totalSeconds = Math.floor(duration / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes} min ${seconds} sec`;
  };

  const columns = [
    {
      title: 'Build #',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        // Enhanced status badge styling
        const statusConfig = {
          SUCCESS: {
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            borderColor: 'border-green-200',
            icon: '✓'
          },
          FAILURE: {
            bgColor: 'bg-red-100',
            textColor: 'text-red-800', 
            borderColor: 'border-red-200',
            icon: '✗'
          },
          default: {
            bgColor: 'bg-yellow-100',
            textColor: 'text-yellow-800',
            borderColor: 'border-yellow-200',
            icon: '⟳'
          }
        };
        
        const config = statusConfig[status] || statusConfig.default;
        
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor} border ${config.borderColor} flex items-center justify-center w-24`}>
            <span className="mr-1">{config.icon}</span> {status}
          </span>
        );
      },
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => (
        <div className="flex items-center text-gray-700">
          <Clock className="h-4 w-4 mr-2 text-gray-500" />
          {new Date(timestamp).toLocaleString()}
        </div>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => (
        <span className="text-gray-700 font-medium">{calculateDuration(duration)}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, build) => (
        <Space>
          <button
            onClick={() => viewBuildLogs(build)}
            className="inline-flex items-center px-3 py-1.5 border border-indigo-500 text-indigo-600 bg-white rounded-md hover:bg-indigo-50 transition-all duration-200 shadow-sm text-sm font-medium"
          >
            <Eye className="h-4 w-4 mr-1.5 text-indigo-600" />
            View Logs
          </button>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Service Deployment History</h2>
          <p className="text-gray-500 text-sm">Last 10 Deployments</p>
        </div>
        <button
          onClick={startBuild}
          disabled={isLoading || buttonDisabled}
          className="inline-flex items-center text-sm px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-200 text-white rounded-lg transition-all duration-200 font-medium shadow-md disabled:opacity-70 disabled:cursor-not-allowed gap-2"
        >
          <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? 'Building...' : 'Rebuild'}
        </button>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <Table
          columns={columns}
          dataSource={builds}
          loading={isLoading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} builds`,
          }}
        />
      </div>
      
      {/* Log Viewer Modal - Using our new reusable component */}
      <LogViewerModal
        isOpen={!!selectedBuild}
        onClose={() => setSelectedBuild(null)}
        logSource={selectedBuild ? `https://cloudify-prod.wizr.in/${projectId}/service/${serviceId}/build-logs?build_number=${selectedBuild.number}` : ''}
        title={selectedBuild ? `Build #${selectedBuild.number} Logs` : ''}
        buildNumber={selectedBuild?.number}
        timestamp={selectedBuild?.timestamp}
        status={selectedBuild?.status}
        subtitle={selectedBuild ? calculateDuration(selectedBuild.duration) : ''}
      />
    </div>
  );
};

export default Deployment;